/**
 * Textarea component for multi-line text input
 *
 * Styled with Tailwind CSS using glass morphism for dark theme.
 * Supports validation states (error, success, warning).
 */

import type { TextareaOptions, ValidationState } from '../types/form';

/**
 * Base CSS classes for the textarea element
 */
const BASE_CLASSES = [
  'w-full',
  'px-3',
  'py-2',
  'touch-input',
  'text-sm',
  'text-foreground',
  'placeholder:text-muted-foreground',
  'rounded-md',
  'border',
  'bg-neutral-950/60',
  'backdrop-blur-sm',
  'transition-colors',
  'duration-200',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'focus:ring-offset-background',
  'disabled:cursor-not-allowed',
  'disabled:opacity-50',
  'min-h-[80px]',
].join(' ');

/**
 * CSS classes for validation states
 */
const STATE_CLASSES: Record<ValidationState, string> = {
  default:
    'border-neutral-800 hover:border-neutral-700 focus:border-primary focus:ring-primary/20',
  error:
    'border-destructive hover:border-destructive focus:border-destructive focus:ring-destructive/20',
  success:
    'border-success hover:border-success focus:border-success focus:ring-success/20',
  warning:
    'border-warning hover:border-warning focus:border-warning focus:ring-warning/20',
};

/**
 * CSS classes for resize options
 */
const RESIZE_CLASSES: Record<NonNullable<TextareaOptions['resize']>, string> = {
  none: 'resize-none',
  both: 'resize',
  horizontal: 'resize-x',
  vertical: 'resize-y',
};

/**
 * Generate a unique ID if not provided
 */
function generateId(): string {
  return `textarea-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a Textarea element
 */
export function createTextarea(options: TextareaOptions = {}): HTMLTextAreaElement {
  const {
    id = generateId(),
    name,
    value = '',
    placeholder,
    disabled = false,
    required = false,
    readonly = false,
    rows = 3,
    cols,
    minLength,
    maxLength,
    resize = 'vertical',
    className = '',
    validationState = 'default',
    attributes = {},
    onInput,
    onChange,
    onFocus,
    onBlur,
  } = options;

  const textarea = document.createElement('textarea');

  // Set basic attributes
  textarea.id = id;
  textarea.value = value;
  textarea.rows = rows;

  if (name) textarea.name = name;
  if (placeholder) textarea.placeholder = placeholder;
  if (disabled) textarea.disabled = true;
  if (required) textarea.required = true;
  if (readonly) textarea.readOnly = true;
  if (cols !== undefined) textarea.cols = cols;
  if (minLength !== undefined) textarea.minLength = minLength;
  if (maxLength !== undefined) textarea.maxLength = maxLength;

  // Set CSS classes
  const stateClasses = STATE_CLASSES[validationState];
  const resizeClass = RESIZE_CLASSES[resize];
  textarea.className = `${BASE_CLASSES} ${stateClasses} ${resizeClass} ${className}`.trim();

  // Set additional attributes
  for (const [key, val] of Object.entries(attributes)) {
    textarea.setAttribute(key, val);
  }

  // Attach event handlers
  if (onInput) {
    textarea.addEventListener('input', (event) => {
      onInput(textarea.value, event);
    });
  }

  if (onChange) {
    textarea.addEventListener('change', (event) => {
      onChange(textarea.value, event);
    });
  }

  if (onFocus) {
    textarea.addEventListener('focus', (event) => {
      onFocus(event as FocusEvent);
    });
  }

  if (onBlur) {
    textarea.addEventListener('blur', (event) => {
      onBlur(event as FocusEvent);
    });
  }

  return textarea;
}

/**
 * Textarea class for object-oriented usage
 */
export class Textarea {
  private element: HTMLTextAreaElement;
  private options: TextareaOptions;

  constructor(options: TextareaOptions = {}) {
    this.options = { ...options };
    this.element = createTextarea(this.options);
  }

  /**
   * Get the textarea element
   */
  getElement(): HTMLTextAreaElement {
    return this.element;
  }

  /**
   * Get the current value
   */
  getValue(): string {
    return this.element.value;
  }

  /**
   * Set the value
   */
  setValue(value: string): this {
    this.element.value = value;
    return this;
  }

  /**
   * Get the text length
   */
  getLength(): number {
    return this.element.value.length;
  }

  /**
   * Set the validation state
   */
  setValidationState(state: ValidationState): this {
    this.options.validationState = state;
    const stateClasses = STATE_CLASSES[state];

    // Remove all state classes
    Object.values(STATE_CLASSES).forEach((classes) => {
      classes.split(' ').forEach((cls) => {
        this.element.classList.remove(cls);
      });
    });

    // Add new state classes
    stateClasses.split(' ').forEach((cls) => {
      this.element.classList.add(cls);
    });

    return this;
  }

  /**
   * Set disabled state
   */
  setDisabled(disabled: boolean): this {
    this.element.disabled = disabled;
    return this;
  }

  /**
   * Check if disabled
   */
  isDisabled(): boolean {
    return this.element.disabled;
  }

  /**
   * Focus the textarea
   */
  focus(): this {
    this.element.focus();
    return this;
  }

  /**
   * Blur the textarea
   */
  blur(): this {
    this.element.blur();
    return this;
  }

  /**
   * Select all text
   */
  select(): this {
    this.element.select();
    return this;
  }

  /**
   * Check validity
   */
  checkValidity(): boolean {
    return this.element.checkValidity();
  }

  /**
   * Report validity (shows browser validation message)
   */
  reportValidity(): boolean {
    return this.element.reportValidity();
  }

  /**
   * Set custom validity message
   */
  setCustomValidity(message: string): this {
    this.element.setCustomValidity(message);
    return this;
  }

  /**
   * Clear the textarea
   */
  clear(): this {
    this.element.value = '';
    return this;
  }

  /**
   * Auto-resize to fit content
   */
  autoResize(): this {
    this.element.style.height = 'auto';
    this.element.style.height = `${this.element.scrollHeight}px`;
    return this;
  }

  /**
   * Add event listener
   */
  on<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.addEventListener(type, listener);
    return this;
  }

  /**
   * Remove event listener
   */
  off<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.removeEventListener(type, listener);
    return this;
  }
}
